import { $, $$, pageData, setLoading } from '../lib/dom.js';
import { ct } from '../lib/i18n.js';
import { api } from '../lib/api.js';
import { trackDirtyForm } from '../lib/dirty-form.js';
import { findSelectMenu } from '../lib/select-menu.js';
import { toast } from '../lib/toast.js';

/**
 * The Suggestions page.
 *
 * A single form for the settings, and therefore a single save bar: switching
 * tab never loses a change in progress.
 *
 * Ruling on a suggestion escapes that bar. It is not a setting: the status goes
 * out at once, republishes the message and notifies the author. Putting it
 * behind "Save" would suggest it could be undone.
 */

interface Settings {
  enabled: boolean;
  channelId: string;
  acceptedChannelId: string;
  refusedChannelId: string;
  mentionRoleId: string;
  alertThreshold: number;
  openThread: boolean;
  threadAutoArchiveMinutes: number;
  anonymousAuthor: boolean;
  anonymousStaff: boolean;
  notifyAuthor: boolean;
  allowSelfDelete: boolean;
  cooldownSeconds: number;
  maxPending: number;
  minLength: number;
  maxLength: number;
  allowedRoleIds: string[];
  blockedRoleIds: string[];
  staffRoleIds: string[];
}

interface PageData {
  settings: Settings;
  guildId: string;
}

export function initSuggestionsPage(): void {
  const found = $<HTMLFormElement>('[data-suggestions-form]');
  if (!found) return;

  const form: HTMLFormElement = found;
  const { settings, guildId } = pageData<PageData>();

  // The role lists are upgraded menus: their value does not live in the
  // original `<select>`, which `upgradeSelects` replaced.
  for (const field of ['allowedRoleIds', 'blockedRoleIds', 'staffRoleIds'] as const) {
    const menu = findSelectMenu(form, field);
    if (menu) menu.value = settings[field];
  }

  trackDirtyForm({
    form,
    successMessage: ct('settingsSaved', 'Settings saved.'),
    onSave: async () => {
      await api.put(`/api/suggestions?guildId=${guildId}`, {
        guildId,
        enabled: checkbox(form, 'enabled'),

        channelId: findSelectMenu(form, 'channelId')?.single ?? '',
        acceptedChannelId: findSelectMenu(form, 'acceptedChannelId')?.single ?? '',
        refusedChannelId: findSelectMenu(form, 'refusedChannelId')?.single ?? '',
        mentionRoleId: findSelectMenu(form, 'mentionRoleId')?.single ?? '',
        alertThreshold: number(form, 'alertThreshold'),

        openThread: checkbox(form, 'openThread'),
        threadAutoArchiveMinutes: number(form, 'threadAutoArchiveMinutes') || 1440,

        anonymousAuthor: checkbox(form, 'anonymousAuthor'),
        anonymousStaff: checkbox(form, 'anonymousStaff'),
        notifyAuthor: checkbox(form, 'notifyAuthor'),
        allowSelfDelete: checkbox(form, 'allowSelfDelete'),

        cooldownSeconds: number(form, 'cooldownSeconds'),
        maxPending: number(form, 'maxPending'),
        minLength: number(form, 'minLength') || 1,
        maxLength: number(form, 'maxLength') || 1000,

        allowedRoleIds: findSelectMenu(form, 'allowedRoleIds')?.value ?? [],
        blockedRoleIds: findSelectMenu(form, 'blockedRoleIds')?.value ?? [],
        staffRoleIds: findSelectMenu(form, 'staffRoleIds')?.value ?? [],
      });
    },
  });

  wireRegistry(form, guildId);
}

/* ---------------------------------------------------------------- registre */

function wireRegistry(form: HTMLFormElement, guildId: string): void {
  const panel = $<HTMLElement>('[data-handle-panel]', form);
  const label = $<HTMLElement>('[data-handle-number]', form);
  if (!panel || !label) return;

  let current = 0;

  const open = (number: number) => {
    current = number;
    label.textContent = `#${String(number)}`;
    panel.hidden = false;
    panel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  const close = () => {
    current = 0;
    panel.hidden = true;
    const response = $<HTMLInputElement>('[data-handle-response]', panel);
    if (response) response.value = '';
  };

  for (const button of $$<HTMLButtonElement>('[data-handle]', form)) {
    button.addEventListener('click', () => {
      open(Number(button.dataset.handle));
    });
  }

  $<HTMLButtonElement>('[data-handle-cancel]', panel)?.addEventListener('click', close);

  $<HTMLButtonElement>('[data-handle-submit]', panel)?.addEventListener('click', (event) => {
    const button = event.currentTarget as HTMLButtonElement;
    if (!current) return;

    void (async () => {
      setLoading(button, true);
      try {
        await api.post(`/api/suggestions/status?guildId=${guildId}`, {
          guildId,
          number: current,
          status: select(panel, 'handle-status'),
          response: input(panel, 'handle-response'),
        });
        toast(ct('suggestionHandled', 'Suggestion handled.'), 'success');

        // The table is rendered by the server: reloading is what shows the new
        // status without reimplementing the row in the browser.
        location.reload();
      } catch (error) {
        toast(error instanceof Error ? error.message : String(error), 'error');
      } finally {
        setLoading(button, false);
      }
    })();
  });

  $<HTMLButtonElement>('[data-handle-delete]', panel)?.addEventListener('click', (event) => {
    const button = event.currentTarget as HTMLButtonElement;
    if (!current) return;
    if (!confirm(ct('confirmDeleteSuggestion', 'Delete this suggestion?'))) return;

    void (async () => {
      setLoading(button, true);
      try {
        await api.post(`/api/suggestions/delete?guildId=${guildId}`, {
          guildId,
          number: current,
        });
        toast(ct('suggestionDeleted', 'Suggestion deleted.'), 'success');
        location.reload();
      } catch (error) {
        toast(error instanceof Error ? error.message : String(error), 'error');
      } finally {
        setLoading(button, false);
      }
    })();
  });
}

/* ------------------------------------------------------------------- champs */

function number(form: HTMLFormElement, name: string): number {
  const node = form.elements.namedItem(name);
  const value =
    node instanceof HTMLInputElement || node instanceof HTMLSelectElement ? Number(node.value) : 0;
  return Number.isFinite(value) && value >= 0 ? value : 0;
}

function checkbox(form: HTMLFormElement, name: string): boolean {
  const node = form.elements.namedItem(name);
  return node instanceof HTMLInputElement ? node.checked : false;
}

/** The value of a `<select>` in the ruling panel, found by its attribute. */
function select(root: ParentNode, field: string): string {
  const node = root.querySelector(`[data-${field}]`);
  return node instanceof HTMLSelectElement ? node.value : '';
}

function input(root: ParentNode, field: string): string {
  const node = root.querySelector(`[data-${field}]`);
  return node instanceof HTMLInputElement ? node.value.trim() : '';
}
